import { TemplateCategory } from '@heidi/schema/template-gallery';
import type { AxiosError } from 'axios';
import { useMemo } from 'react';

import { useQueryDocumentTemplates } from './query/useQueryDocumentTemplates';

const FORM_TEMPLATE = TemplateCategory.FORM_TEMPLATE as string;

type DocumentTemplateRow = {
  id: string;
  name?: string | null;
  template_category?: string | null;
  form_fields?: unknown[] | null;
};

export type PresetTemplateOption = { id: string; name: string };

export function isFormTemplateCategory(category: string | null | undefined): boolean {
  return category === FORM_TEMPLATE;
}

/** Document templates (excluding PDF forms) for preset setup pickers. */
export function toPresetDocumentTemplateOptions(
  templates: DocumentTemplateRow[] | undefined
): PresetTemplateOption[] {
  return (templates ?? [])
    .filter((t) => !isFormTemplateCategory(t.template_category))
    .map((t) => ({
      id: t.id,
      name: (t.name ?? '').trim() || 'Untitled template',
    }));
}

/** PDF form templates that have at least one field, for preset setup pickers. */
export function toPresetFormTemplateOptions(
  templates: DocumentTemplateRow[] | undefined
): PresetTemplateOption[] {
  return (templates ?? [])
    .filter(
      (t) => isFormTemplateCategory(t.template_category) && Boolean(t.form_fields?.length)
    )
    .map((t) => ({
      id: t.id,
      name: (t.name ?? '').trim() || 'Untitled form',
    }));
}

/**
 * Headless: auth + desktop readiness + error toast are composed app-side
 * (same port shape as `useQueryDocumentTemplates`).
 */
export function usePresetDocumentTemplateOptions(
  isAuthenticated: boolean,
  isDesktopOnlineReady: boolean,
  onRequestError: (error: AxiosError) => void
): PresetTemplateOption[] {
  const { data: templates } = useQueryDocumentTemplates(
    isAuthenticated,
    isDesktopOnlineReady,
    onRequestError
  );
  return useMemo(() => toPresetDocumentTemplateOptions(templates), [templates]);
}

/**
 * Headless: auth + desktop readiness + error toast are composed app-side
 * (same port shape as `useQueryDocumentTemplates`).
 */
export function usePresetFormTemplateOptions(
  isAuthenticated: boolean,
  isDesktopOnlineReady: boolean,
  onRequestError: (error: AxiosError) => void
): PresetTemplateOption[] {
  const { data: templates } = useQueryDocumentTemplates(
    isAuthenticated,
    isDesktopOnlineReady,
    onRequestError
  );
  return useMemo(() => toPresetFormTemplateOptions(templates), [templates]);
}
